import { DataFormats } from '@skylab-shared/constants';
import { HttpService, Query, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { SaleDocumentKind } from '@skylab-shared/dataModel';
import { MS_PER_MINUTE, StringUtils } from '@skylab-shared/utils';
import { Transform } from 'class-transformer';
import { format } from 'fecha';
import { InvoiceToReceiptTag } from '../../common/invoice-to-receipt-tag';
import { CommunityTransactionForSaleQuery, SaleCommunityTransactionItem } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';

export interface SalesByPeriodParameters {
    convertToPln: boolean;
    issueDateFrom: Date;
    issueDateTo: Date;
    saleDateFrom: Date;
    saleDateTo: Date;
    dueDateFrom: Date;
    dueDateTo: Date;
    taxLiabilityDateFrom: Date;
    taxLiabilityDateTo: Date;
    clientGroupId?: string;
    noClientGroup?: boolean;
    kinds: SaleDocumentKind[];
    jpkVatDocumentGroup?: string;
    jpkVatProductGroup?: string;
    communityTransactionId: number;
    invoiceToReceipt?: boolean;
    omitInvoiceIssuedToReceipt: boolean;
    search: string;
    paymentMethodId: number;
}

export interface SalesByPeriodModel {
    adjustment: boolean;
    baseCurrencyGross: number;
    baseCurrencyId: string;
    baseCurrencyNet: number;
    baseCurrencyVat: number;
    client: string;
    clientAddress: string | null;
    clientCountry?: string;
    clientGroup: string;
    currencyId: string;
    documentId: number;
    documentKind: SaleDocumentKind;
    documentNumber: string;
    gross: number;
    issueDate: Date;
    saleDate: Date;
    dueDate: Date;
    taxLiabilityDate: Date;
    net: number;
    tin: string;
    vat: number;
    paymentMethodName?: string;
    communityTransaction?: SaleCommunityTransactionItem;
    jpkVatGroups?: string | null;
}

class SalesByPeriodInternalItemDTO {
    public readonly adjustment!: boolean;
    public readonly baseCurrencyGross!: number;
    public readonly baseCurrencyId!: string;
    public readonly baseCurrencyNet!: number;
    public readonly baseCurrencyVat!: number;
    public readonly clientAddress!: {
        address: string;
        city: string;
        countryId: number;
        name: string;
        zipCode: string;
    };
    public readonly clientGroup!: string;
    public readonly clientName!: string;
    public readonly communityTransactionId!: number;
    public readonly currencyId!: string;
    public readonly documentId!: number;
    public readonly documentKind!: SaleDocumentKind;
    public readonly documentNumber!: string;
    public readonly gross!: number;
    public readonly invoiceToReceipt!: boolean;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate!: Date;
    public readonly jpkVatDocumentGroups!: number;
    public readonly jpkVatProductGroups!: string;
    public readonly kind!: SaleDocumentKind;
    public readonly net!: number;
    public readonly paymentMethodId!: number;
    public readonly precision!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly saleDate!: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate!: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly taxLiabilityDate!: Date;
    public readonly tin!: string;
    public readonly vat!: number;
}

export class SalesByPeriodCollectionQuery extends Query<SalesByPeriodModel[]> {
    public constructor(public readonly parameters: SalesByPeriodParameters) {
        super();
    }
}

export class SalesByPeriodCollectionQueryHandlerBase implements QueryHandler<SalesByPeriodCollectionQuery, SalesByPeriodModel[]> {
    private static Url = TaggedUrl.join('salesByPeriod', [Tags.Api]);

    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: SalesByPeriodCollectionQuery): Promise<SalesByPeriodModel[]> {
        const { data } = await this.http.get<SalesByPeriodInternalItemDTO[]>(SalesByPeriodCollectionQueryHandlerBase.Url, {
            params: {
                ...query.parameters,
                issueDateFrom: query.parameters.issueDateFrom && format(query.parameters.issueDateFrom, DataFormats.date.webApi),
                issueDateTo: query.parameters.issueDateTo && format(query.parameters.issueDateTo, DataFormats.date.webApi),
                saleDateFrom: query.parameters.saleDateFrom && format(query.parameters.saleDateFrom, DataFormats.date.webApi),
                saleDateTo: query.parameters.saleDateTo && format(query.parameters.saleDateTo, DataFormats.date.webApi),
                dueDateFrom: query.parameters.dueDateFrom && format(query.parameters.dueDateFrom, DataFormats.date.webApi),
                dueDateTo: query.parameters.dueDateTo && format(query.parameters.dueDateTo, DataFormats.date.webApi),
                taxLiabilityDateFrom: query.parameters.taxLiabilityDateFrom && format(query.parameters.taxLiabilityDateFrom, DataFormats.date.webApi),
                taxLiabilityDateTo: query.parameters.taxLiabilityDateTo && format(query.parameters.taxLiabilityDateTo, DataFormats.date.webApi),
            },
            timeout: 3 * MS_PER_MINUTE,
            dto: SalesByPeriodInternalItemDTO
        });

        const paymentMethods = await this.rootContext.getAsync(PaymentMethodQuery);
        const communityTransactions = await this.rootContext.getAsync(CommunityTransactionForSaleQuery);
        const countries = await this.rootContext.getAsync(CountryCollectionQuery);

        return data.map(item => {
            const invoiceToReceiptTag = item.invoiceToReceipt ? InvoiceToReceiptTag.code : null;
            return {
                adjustment: item.adjustment,
                baseCurrencyId: item.baseCurrencyId,
                baseCurrencyGross: item.baseCurrencyGross,
                baseCurrencyNet: item.baseCurrencyNet,
                baseCurrencyVat: item.baseCurrencyVat,
                client: item.clientName,
                clientAddress: item.clientAddress
                    ? StringUtils.join(', ', item.clientAddress.address, item.clientAddress.zipCode, item.clientAddress.city)
                    : null,
                clientCountry: countries.find(elem => elem.id === item.clientAddress?.countryId)?.name,
                clientGroup: item.clientGroup,
                currencyId: item.currencyId,
                documentId: item.documentId,
                documentKind: item.documentKind,
                documentNumber: item.documentNumber,
                gross: item.gross,
                issueDate: item.issueDate,
                saleDate: item.saleDate,
                dueDate: item.dueDate,
                taxLiabilityDate: item.taxLiabilityDate,
                net: item.net,
                tin: item.tin,
                vat: item.vat,
                paymentMethodName: paymentMethods.find(elem => elem.id === item.paymentMethodId)?.name,
                communityTransaction: communityTransactions.find(elem => elem.id === item.communityTransactionId),
                jpkVatGroups: [item.jpkVatProductGroups, item.jpkVatDocumentGroups, invoiceToReceiptTag].filter(Boolean).join(', ') || null
            };
        });
    }
}
